import type { DrawRecord, PickerEntry, PickscapeProject } from "../types";
import { EMPTY_PROJECT } from "../types";

export function createEmptyProject(): PickscapeProject {
  return {
    ...EMPTY_PROJECT,
    entries: [],
    history: [],
    settings: {
      ...EMPTY_PROJECT.settings,
      selectedCategories: [],
    },
    updatedAt: new Date().toISOString(),
  };
}

export function getCategories(entries: PickerEntry[]): string[] {
  return Array.from(
    new Set(entries.map((entry) => entry.category).filter((value): value is string => Boolean(value))),
  ).sort((a, b) => a.localeCompare(b));
}

export function getEligibleEntries(project: PickscapeProject): PickerEntry[] {
  const selected = new Set(project.settings.selectedCategories);
  const filtered = project.entries.filter(
    (entry) =>
      entry.enabled &&
      (selected.size === 0 || (entry.category !== null && selected.has(entry.category))),
  );
  if (project.settings.repeatMode === "repeat") return filtered;

  const latestRetentionByWinner = new Map<string, boolean>();
  project.history.forEach((record) => {
    latestRetentionByWinner.set(record.winnerId, record.keptInPool === true);
  });
  return filtered.filter((entry) => latestRetentionByWinner.get(entry.id) !== false);
}

export function keepLatestWinnerInPool(
  history: DrawRecord[],
  winnerId: string,
): DrawRecord[] {
  let latestIndex = -1;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.winnerId === winnerId) {
      latestIndex = index;
      break;
    }
  }
  if (latestIndex < 0) return history;
  return history.map((record, index) =>
    index === latestIndex ? { ...record, keptInPool: true } : record,
  );
}

export function mergeEntries(
  currentEntries: PickerEntry[],
  incomingEntries: PickerEntry[],
  mode: "replace" | "append",
): PickerEntry[] {
  return mode === "replace" ? incomingEntries : [...currentEntries, ...incomingEntries];
}
